import React, { useEffect, useState } from 'react';
import { Box, Tabs } from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import TabPanelDropZone from './TabPanelDropZone';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import { puckArrayFieldPath } from '../../../utils/puckContentPath';
import { subscribeTabPreview } from '../../../utils/tabsRegistry';
import { useAppearOnScroll } from '../../../hooks/useAppearOnScroll';
import appearStyles from '../../../hooks/blockAppearance.module.css';
import {
  APPEAR_VARIANT_CLASS,
  isAppearAnimationActive,
  normalizeAppearAnimation,
  normalizeAppearAnimationDuration,
} from '../../../utils/appearAnimations';
import { resolveActiveTabId, tabPlainLabel, tabsListJustify } from './tabsLayout';
import { PAGE_CONTENT_GUTTER_PX, PAGE_CONTENT_MAX_WIDTH_PX } from '../../../layout/pageLayoutConstants';
import { cssLengthToPx } from '../../../utils/proposalUnits';
import { sanitizeRichHtml } from '../../../utils/sanitizeRichHtml';
import styles from './TabsBlock.module.css';

/** Couleur vide = l'icône suit la couleur du texte de l'onglet. */
const TabIcon = ({ name, color }) => {
  const Icon = name ? TablerIcons[name] : null;
  return Icon ? <Icon size={16} stroke={1.5} color={color || undefined} /> : null;
};

/**
 * Libellé rendu sur le site. Le texte riche est injecté tel quel (gras,
 * couleur…) ; vidé, l'onglet retombe sur « Onglet N » plutôt que de devenir un
 * bouton invisible.
 */
const TabLabel = ({ tab, index }) => {
  const html = String(tab?.label || '').trim();
  const isEmpty = !html.replace(/<[^>]+>/g, '').trim();
  if (isEmpty) return tabPlainLabel(tab, index);
  // eslint-disable-next-line react/no-danger
  return <span dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }} />;
};

/**
 * Bloc Onglets — plusieurs contenus dans le même espace, le visiteur bascule.
 *
 * Chaque onglet contient exactement un Bloc (`ProposalSection`) : un Bloc sait
 * déjà tout faire (colonnes, fond, marges, animation), donc un onglet affiche
 * n'importe quoi sans qu'aucune règle de disposition ne bouge.
 *
 * Il se pose à la racine de la page, pas dans une colonne : c'est lui qui
 * héberge des Blocs, il ne peut pas en être une box.
 *
 * Tous les panneaux restent MONTÉS (`keepMounted` de Mantine, actif par
 * défaut), l'inactif est masqué : une DropZone démontée casse le
 * glisser-déposer et les verrous de collaboration. C'est aussi le motif ARIA
 * correct (`role="tabpanel"` + `hidden`).
 */
export default function TabsBlock({
  id,
  tabs = [],
  /** 'default' (onglets classiques) | 'pills'. */
  variant = 'default',
  /** Alignement de la barre : 'left' | 'center' | 'right'. */
  listAlign = 'left',
  /** Couleur de l'onglet actif — vide = couleur principale du thème. */
  accentColor = '',
  /** Onglet ouvert au chargement de la page. */
  defaultTabId = '',
  /* Réglages de proximité à la page : ils sont portés ici pour l'ensemble des
     onglets, et retirés des Blocs imbriqués (cf. nestedSectionFields.js). */
  appearAnimation = 'none',
  appearAnimationDuration,
  marginBottom = '',
  /** Gouttière vers les bords de la page. 0 = la zone traverse la page. */
  paddingX,
  paddingTop = 0,
  paddingBottom = 0,
  /** Style par défaut des libellés d'onglets (rôle `label`). */
  textStyles = null,
  isVisible = true,
  puck,
}) {
  const isEditing = !!puck?.isEditing;
  // Onglet cliqué : état LOCAL au canvas. Il n'est lu par aucun parent, donc
  // aucun `setState` ne remonte hors du bloc (invariants de perf, README §4).
  const [requestedTabId, setRequestedTabId] = useState(null);
  const activeTabId = resolveActiveTabId(tabs, requestedTabId, defaultTabId);

  // Changer l'onglet par défaut dans la sidebar doit se voir sur le canvas :
  // sans ça, le clic précédent gagnerait pour toujours.
  useEffect(() => { setRequestedTabId(null); }, [defaultTabId]);

  // L'œil d'un onglet dans la sidebar l'ouvre sur le canvas : replier un item
  // n'aide pas à régler un onglet qu'on ne voit pas.
  useEffect(() => {
    if (!isEditing) return undefined;
    return subscribeTabPreview((blockId, tabId) => {
      if (blockId === id) setRequestedTabId(tabId);
    });
  }, [isEditing, id]);

  const appear = normalizeAppearAnimation(appearAnimation);
  const appearActive = isAppearAnimationActive(appear, { isEditing });
  const { ref: appearRef, visible: appearVisible } = useAppearOnScroll(appearActive);
  const appearClassName = appearActive
    ? `${appearStyles.appear} ${appearStyles[APPEAR_VARIANT_CLASS[appear]]} ${appearVisible ? '' : appearStyles.appearHidden}`
    : '';

  if (!isVisible && !isEditing) return null;
  if (!activeTabId) return null;

  // Même contrat que le Bloc : la prop absente vaut la gouttière historique,
  // et 0 retire la largeur max — le contenu va d'un bord à l'autre.
  const gutterPx = paddingX === undefined
    ? PAGE_CONTENT_GUTTER_PX
    : (cssLengthToPx(paddingX) || 0);

  return (
    <Box
      /* Un bloc Onglets est une cible de lien au même titre qu'un Bloc, mais
         sa racine ne portait aucun id : `getElementById` ne trouvait rien et le
         bouton ne faisait rien du tout. L'id n'est posé que sur la racine —
         ailleurs dans ce composant il ne sert qu'à adresser Puck. */
      id={id}
      ref={appearRef}
      className={`${styles.root} ${appearClassName} ${isEditing ? styles.rootEditing : ''}`.trim()}
      w="100%"
      mx="auto"
      px={gutterPx}
      style={{
        maxWidth: gutterPx ? `${PAGE_CONTENT_MAX_WIDTH_PX}px` : undefined,
        boxSizing: 'border-box',
        paddingTop: paddingTop || undefined,
        paddingBottom: paddingBottom || undefined,
        marginBottom: marginBottom || undefined,
        ...(appearActive
          ? { '--appear-duration': `${normalizeAppearAnimationDuration(appearAnimationDuration)}ms` }
          : null),
      }}
    >
      <Tabs
        value={activeTabId}
        onChange={setRequestedTabId}
        variant={variant === 'pills' ? 'pills' : 'default'}
        color={accentColor || undefined}
      >
        <Tabs.List
          className={styles.list}
          style={{ justifyContent: tabsListJustify(listAlign) }}
        >
          {tabs.map((tab, index) => (
            <Tabs.Tab
              key={tab.id}
              // Mantine pose role, tabIndex, aria-* et la navigation aux flèches
              // sur l'élément, quel qu'il soit : en div, l'onglet garde tout ça
              // sans la sémantique <button>, qui avale Entrée et Espace dans le
              // contenteditable du libellé.
              component={isEditing ? 'div' : undefined}
              value={tab.id}
              className={styles.tab}
              style={textRoleCss(textStyles, 'label')}
              leftSection={<TabIcon name={tab.icon} color={tab.iconColor} />}
            >
              {isEditing ? (
                // Les flèches déplacent le curseur dans le texte ; sans ce
                // barrage, le gestionnaire clavier de Mantine les lirait comme
                // « onglet suivant » et sauterait d'onglet en pleine frappe.
                <span onKeyDown={(e) => e.stopPropagation()}>
                  <PuckInlineRichText
                    id={id}
                    puck={puck}
                    html={tab.label}
                    contentPath={puckArrayFieldPath('tabs', index, 'label')}
                    placeholder="Onglet"
                    drawMode
                    showPlaceholderChip
                    // La barre d'onglets défile en x et masque son débordement
                    // en y : sans positionnement viewport, elle coupait la barre
                    // d'outils du libellé.
                    bubbleStrategy="fixed"
                    reducedToolbar
                  />
                </span>
              ) : <TabLabel tab={tab} index={index} />}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabs.map((tab) => (
          <Tabs.Panel key={tab.id} value={tab.id} pt="md">
            <TabPanelDropZone
              isEditing={isEditing}
              blockId={id}
              tabId={tab.id}
              className={`${styles.panelZone} ${isEditing ? styles.panelZoneEditing : ''}`.trim()}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  );
}
